import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { CalendarDays, Plus, Trash2, Loader2, AlertCircle, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface LocalHoliday {
  id: string;
  date: string;
  name: string;
  year: number;
}

const MAX_LOCAL_HOLIDAYS_PER_YEAR = 2;

export function LocalHolidaysManager() {
  const { user, profile } = useAuth();
  const [holidays, setHolidays] = useState<LocalHoliday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [holidayName, setHolidayName] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const organizationId = (profile as any)?.organization_id;
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  // Conteo de festivos por año
  const countByYear = holidays.reduce((acc, h) => {
    acc[h.year] = (acc[h.year] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const loadLocalHolidays = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      let query = supabase
        .from('holidays')
        .select('id, date, name, year')
        .eq('scope', 'local')
        .in('year', [currentYear, nextYear])
        .order('date', { ascending: true });

      // Si tiene organización, filtrar por ella
      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setHolidays((data || []) as LocalHoliday[]);
    } catch (error) {
      console.error('Error loading local holidays:', error);
      toast.error('Error al cargar los festivos locales');
    } finally {
      setIsLoading(false);
    }
  }, [user, organizationId, currentYear, nextYear]);

  useEffect(() => {
    loadLocalHolidays();
  }, [loadLocalHolidays]);

  const canAddHoliday = (date: Date): boolean => {
    const year = date.getFullYear();
    if (year !== currentYear && year !== nextYear) return false;
    return (countByYear[year] || 0) < MAX_LOCAL_HOLIDAYS_PER_YEAR;
  };

  const addLocalHoliday = async () => {
    if (!selectedDate || !holidayName.trim() || !user) {
      toast.error('Por favor, selecciona una fecha y escribe el nombre del festivo');
      return;
    }

    const year = selectedDate.getFullYear();
    if (!canAddHoliday(selectedDate)) {
      toast.error(`Ya tienes ${MAX_LOCAL_HOLIDAYS_PER_YEAR} festivos locales para ${year}`);
      return;
    }

    setIsSaving(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('holidays')
        .insert({
          date: dateStr,
          name: holidayName.trim(),
          scope: 'local' as const,
          year,
          organization_id: organizationId || null,
        })
        .select('id, date, name, year')
        .single();

      if (error) throw error;

      setHolidays(prev => [...prev, data as LocalHoliday].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      ));
      setSelectedDate(undefined);
      setHolidayName('');
      setIsCalendarOpen(false);
      toast.success('Festivo local añadido');
    } catch (error) {
      console.error('Error adding local holiday:', error);
      toast.error('Error al añadir el festivo local');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteLocalHoliday = async (id: string) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', id)
        .eq('scope', 'local');

      if (error) throw error;

      setHolidays(prev => prev.filter(h => h.id !== id));
      toast.success('Festivo local eliminado');
    } catch (error) {
      console.error('Error deleting local holiday:', error);
      toast.error('Error al eliminar el festivo local');
    } finally {
      setIsSaving(false);
    }
  };

  const holidaysCurrentYear = holidays.filter(h => h.year === currentYear);
  const holidaysNextYear = holidays.filter(h => h.year === nextYear);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Festivos locales
        </CardTitle>
        <CardDescription>
          Añade hasta {MAX_LOCAL_HOLIDAYS_PER_YEAR} festivos locales de tu municipio por año. 
          Estos días no contarán para el cálculo de días hábiles.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Formulario para añadir */}
        <div className="p-4 border rounded-lg bg-muted/30 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Plus className="h-4 w-4" />
            Añadir festivo local
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="holiday-name">Nombre del festivo</Label>
              <Input
                id="holiday-name"
                placeholder="Ej: Feria de San Juan"
                value={holidayName}
                onChange={(e) => setHolidayName(e.target.value)}
                disabled={isSaving}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground"
                    )}
                    disabled={isSaving}
                  >
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {selectedDate 
                      ? format(selectedDate, "PPP", { locale: es })
                      : "Seleccionar fecha"
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      if (date) setIsCalendarOpen(false);
                    }}
                    disabled={(date) => {
                      const year = date.getFullYear();
                      return year < currentYear || year > nextYear;
                    }}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {selectedDate && !canAddHoliday(selectedDate) && (
            <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-4 w-4" />
              Ya tienes {MAX_LOCAL_HOLIDAYS_PER_YEAR} festivos para {selectedDate.getFullYear()}
            </div>
          )}

          <Button
            onClick={addLocalHoliday}
            disabled={isSaving || !selectedDate || !holidayName.trim() || (selectedDate && !canAddHoliday(selectedDate))}
            size="sm"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Añadiendo...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Añadir festivo
              </>
            )}
          </Button>
        </div>

        {/* Lista de festivos por año */}
        <div className="space-y-4">
          {/* Año actual */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{currentYear}</h4>
              <Badge variant={holidaysCurrentYear.length >= MAX_LOCAL_HOLIDAYS_PER_YEAR ? "secondary" : "outline"}>
                {holidaysCurrentYear.length}/{MAX_LOCAL_HOLIDAYS_PER_YEAR}
              </Badge>
            </div>
            
            {holidaysCurrentYear.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No hay festivos locales para {currentYear}
              </p>
            ) : (
              <div className="space-y-2">
                {holidaysCurrentYear.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <CalendarDays className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{holiday.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(holiday.date), "EEEE, d 'de' MMMM", { locale: es })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteLocalHoliday(holiday.id)}
                      disabled={isSaving}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Año siguiente */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{nextYear}</h4>
              <Badge variant={holidaysNextYear.length >= MAX_LOCAL_HOLIDAYS_PER_YEAR ? "secondary" : "outline"}>
                {holidaysNextYear.length}/{MAX_LOCAL_HOLIDAYS_PER_YEAR}
              </Badge>
            </div>
            
            {holidaysNextYear.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No hay festivos locales para {nextYear}
              </p>
            ) : (
              <div className="space-y-2">
                {holidaysNextYear.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <CalendarDays className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{holiday.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(holiday.date), "EEEE, d 'de' MMMM", { locale: es })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteLocalHoliday(holiday.id)}
                      disabled={isSaving}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Los festivos locales se suman a los nacionales y autonómicos para el cálculo de días hábiles en tus plazos.
        </p>
      </CardContent>
    </Card>
  );
}
